import { el } from './dom.js';

/**
 * The "i" (PLAYTEST_LOG 2026-10-29): what a screen used to say in sentences,
 * kept for whoever wants it. The screen itself shows icons and numbers; the
 * sentences open in a bubble under this button, and close at a tap anywhere
 * else, a scroll or Escape. One bubble at a time.
 */

let open: { readonly button: HTMLElement; readonly bubble: HTMLElement } | null = null;
let listening = false;

function close(): void {
  if (!open) return;
  open.bubble.remove();
  open.button.setAttribute('aria-expanded', 'false');
  open = null;
}

function listen(): void {
  if (listening) return;
  listening = true;
  document.addEventListener(
    'pointerdown',
    (event) => {
      if (!open) return;
      const target = event.target as Node;
      if (open.bubble.contains(target) || open.button.contains(target)) return;
      close();
    },
    true,
  );
  document.addEventListener('scroll', close, true);
  window.addEventListener('resize', close);
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') close();
  });
}

/** Under the button, inside the window, as wide as a phone allows. */
function place(button: HTMLElement, bubble: HTMLElement): void {
  const at = button.getBoundingClientRect();
  const width = Math.min(280, window.innerWidth - 24);
  bubble.style.width = `${width}px`;
  const left = Math.max(
    12,
    Math.min(at.left + at.width / 2 - width / 2, window.innerWidth - width - 12),
  );
  bubble.style.left = `${left}px`;
  const below = at.bottom + 8;
  bubble.style.top = `${below}px`;
  // No room underneath: above the button instead.
  const tall = bubble.offsetHeight;
  if (below + tall > window.innerHeight - 12)
    bubble.style.top = `${Math.max(12, at.top - 8 - tall)}px`;
}

/** Opens `title` and its `lines` under `button` at a tap, or closes them at a second. */
function opens(button: HTMLElement, title: string, lines: readonly string[]): void {
  listen();
  button.setAttribute('aria-expanded', 'false');
  button.addEventListener('click', (event) => {
    // A card that is itself a button (a floor, a tile) does not take this tap.
    event.stopPropagation();
    const same = open?.button === button;
    close();
    if (same) return;
    const bubble = el('div', { class: 'info-bubble', role: 'note' }, [
      el('strong', { text: title }),
      ...lines.filter((line) => line !== '').map((line) => el('p', { text: line })),
    ]);
    document.body.append(bubble);
    place(button, bubble);
    button.setAttribute('aria-expanded', 'true');
    open = { button, bubble };
  });
}

/** An "i" that opens `title` and its `lines`. */
export function infoButton(title: string, lines: readonly string[]): HTMLButtonElement {
  const button = el(
    'button',
    { class: 'info-button', type: 'button', 'aria-label': `About ${title}` },
    ['i'],
  );
  opens(button, title, lines);
  return button;
}

/**
 * A sign that says what it means at a tap (the owner, PLAYTEST_LOG 2026-10-30):
 * icons alone left a player before a shift understanding nothing.
 */
export function tipButton(
  className: string,
  title: string,
  lines: readonly string[],
  children: (Node | string)[],
): HTMLButtonElement {
  const button = el(
    'button',
    { class: `${className} tip`, type: 'button', 'data-tip': '' },
    children,
  );
  opens(button, title, lines);
  return button;
}

/** Close whatever bubble is open: a screen changing under it. */
export function closeInfo(): void {
  close();
}
